/**
 * Which promotions still belong on the player's "available" list.
 *
 * The list answers one question — what can I take up right now — so an offer
 * the player has already used up does not belong on it. A finished single-grant
 * bonus is exactly that: the server refuses a second claim, so the card's only
 * remaining affordance is a link to the withdraw page, and the offer it appears
 * to make no longer exists.
 *
 * A claim that is still running is the opposite case and stays: the player has
 * bonus money working off turnover under that card and needs it to see the
 * progress and reach the next step. The difference is the claim's bucket, not
 * the mere existence of a claim.
 *
 * Unknown statuses keep their card. Hiding is the destructive direction — a
 * status this build has never heard of must not silently remove a live offer.
 *
 * A promotion the server says this member may no longer START is hidden only when
 * they hold no claim on it, or one that has already ended — `memberEligible`,
 * below. It never overrides the two rules above it.
 *
 * The end-date check runs BEFORE the claim check on purpose, so a promotion
 * whose window closed under an in-flight claim disappears from this list. Do
 * not reorder it: the player list endpoint forces `activeNow` for non-operators
 * and has already stopped returning that promotion, so keeping the card here
 * would only make the two disagree until the next refetch. The claim itself is
 * not hidden — `ActiveBonusHero` reads active claims, not promotions, and still
 * shows its turnover progress.
 */
import { bucketOf, blocksSingleGrantReclaim } from './claim-buckets.js';
import { isPromoExpired, type Promotion, type PromotionClaim } from './api.js';

/**
 * Kinds that are not an offer this list can make. The claim endpoint refuses
 * both with PROMO-002 (no branch in `claimPromotion`'s kind switch), so a card
 * here would carry a button that can only fail: 'by_admin' is a coupon campaign,
 * handed out by code and never self-served, and 'deposit' is contract-reserved
 * and unproductised. Listing either is the same defect as listing a finished
 * bonus — an offer the player cannot take up.
 *
 * 'display_only' is deliberately NOT here. It is equally unclaimable, but the
 * name says its whole purpose is to appear, and hiding is the destructive
 * direction: silencing a banner an operator meant to publish is worse than the
 * dead button it currently shows.
 */
const NOT_SELF_CLAIMABLE_KINDS = new Set(['by_admin', 'deposit']);

export function isPromoStillOffered(
  promo: Promotion,
  claim: PromotionClaim | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (NOT_SELF_CLAIMABLE_KINDS.has(promo.kind)) return false;
  if (isPromoExpired(promo, nowMs)) return false;
  // The server's per-member verdict, and it answers "may this member START a new
  // claim" — nothing more. A member who deposited without taking the
  // first-deposit promo holds no claim at all, so every rule below answers
  // "still offered" and the card stays up making an offer the claim endpoint
  // refuses; that is the reported defect (#149) and this is what closes it.
  //
  // It is deliberately NOT a veto over a claim the member is already living
  // through. The flag flips to `false` the instant their first deposit is
  // approved — the same instant a legitimate reservation is about to become a
  // live bonus — so overruling the claim rules would delete the card from under
  // the member who did everything right: the reservation waiting on the
  // deposit-approved consumer (`ActiveBonusHero` skips those on purpose, so it
  // would show in neither section of the page) and the bonus still working off
  // its turnover, whose wager CTA lives on this card.
  //
  // A claim in the HISTORY bucket is not that. It buys nothing back: a
  // reservation the sweep retires to 'rejected' would otherwise free the slot
  // below and put the dead offer straight back on the page.
  //
  // The two hiding cases are therefore named POSITIVELY — no claim, or a claim
  // that has ended — rather than written as "not active". The difference is the
  // status this build cannot bucket, and the file's rule for that one is
  // unconditional (see the header): it may well be a live claim, so it keeps its
  // card here too. The cost of showing is bounded — the claim endpoint refuses
  // with PROMO-064 and the player reads a translated sentence saying why — while
  // the cost of hiding is a bonus the member is in the middle of vanishing from
  // the only screen that lists it.
  //
  // `false` is the only value that hides. Absent means the server did not
  // evaluate it — see `Promotion.memberEligible` — and must change nothing.
  if (promo.memberEligible === false && (claim == null || bucketOf(claim.status) === 'history')) {
    return false;
  }
  if (!claim) return true;
  if (bucketOf(claim.status) !== 'history') return true;
  // Terminal claim: only a rejection frees the promotion's per-member slot,
  // which is the same rule the claim guard applies server-side.
  return !blocksSingleGrantReclaim(claim.status);
}

/** The bonus-hub tabs that list promotion cards, in tab order. */
export const PROMO_GROUPS = ['promotions', 'checkin', 'cashback'] as const;
export type PromoGroup = (typeof PROMO_GROUPS)[number];

/** The bonus-hub tabs whose history button opens the history screen. */
export const HISTORY_TABS = [...PROMO_GROUPS, 'spin', 'redeem'] as const;
export type HistoryTab = (typeof HISTORY_TABS)[number];

/**
 * Which bonus-hub tab lists a promotion (or a claim, by its `promotionKind`).
 * Anything that is not check-in or a rebate is a bonus offer, so an unknown or
 * absent kind lands on the promotions tab rather than disappearing.
 */
export function promoGroupOf(kind: string | null | undefined): PromoGroup {
  if (kind === 'daily_checkin') return 'checkin';
  if (kind === 'cashback' || kind === 'turnover_rebate') return 'cashback';
  return 'promotions';
}
